import { isNativeApp, isBundledNativeOrigin } from './appOrigin';

/**
 * Auth0's `openUrl` hook for the packaged app — or `undefined` everywhere else.
 *
 * By default the Auth0 SDK sends the browser to the authorize URL with
 * `window.location.assign()`. Inside the packaged app that navigates the WebView itself,
 * away from the bundled assets and onto login.ipolo.org. The app has no chrome, so the
 * user is stranded on a page with no way back, and the custom-scheme redirect at the end
 * cannot re-enter a WebView that is already showing a remote origin.
 *
 * Opening the system browser instead is also what Auth0 and Apple require: an OAuth
 * redirect flow for a native app is supposed to use the platform's secure browser
 * (Custom Tabs on Android, SFSafariViewController on iOS) rather than an embedded WebView,
 * so the user can see the real address bar and any existing SSO session is reused.
 *
 * Returns `undefined` on the web and under the Phase 0 diagnostic, and the SDK falls back
 * to its normal `window.location.assign()` — so web behaviour is untouched. Gated on the
 * *bundled* origin for the same reason as `resolveAuthRedirectUri`: Phase 0 runs the
 * bridge at a real origin where the ordinary redirect is correct. See appOrigin.ts.
 *
 * `@capacitor/browser` is imported dynamically so that web builds never pull the native
 * plugin into their main bundle — the same reasoning that keeps `isNativeApp()` free of
 * a `@capacitor/core` import.
 */
export function nativeAuthOpenUrl(): ((url: string) => Promise<void>) | undefined {
   if (!isNativeApp() || !isBundledNativeOrigin()) return undefined;

   return async (url: string) => {
      const { Browser } = await import('@capacitor/browser');

      await Browser.open({ url });
   };
}
